import { useCallback, useEffect, useMemo } from 'react';
import {
    STATUS, EVENT_TYPES, MESSAGE_TYPES, RESPONSE_TYPES
} from 'ai/constants/superchat';
import genAISocketService from 'ai/services/socket/socketService';

const useSuperChatSocket = ({
    entryPointDataRef,
    setIsLoading,
    setIsChatInitialized,
    setHasSelectedPrompt,
    setMessages,
    setLatestContextualChipIndex,
    resetHiddenChips,
    setLoadingState,
    onError,
    onInitialize
}) => {
    const handleSocketMessage = useCallback(
        event => {
            if (event.type === EVENT_TYPES.CONNECTION) {
                switch (event.status) {
                    case STATUS.ERROR:
                        genAISocketService.closeConnection();
                        onError?.();

                        break;
                    case STATUS.CONNECTED:
                        Sephora.logger.verbose('SuperChat: WebSocket connected.');

                        if (!event.initializedChat) {
                            Sephora.logger.verbose('SuperChat: No previous session found, sending initial connection...');

                            const entryPointDataWithoutPrompt = { ...entryPointDataRef.current };
                            delete entryPointDataWithoutPrompt.selectedPrompt;

                            genAISocketService.sendInitialConnection(entryPointDataWithoutPrompt);
                            setIsChatInitialized(false);

                            if (entryPointDataRef.current?.selectedPrompt) {
                                setHasSelectedPrompt(true);
                            }
                        } else {
                            setIsLoading(false);
                            setIsChatInitialized(true);

                            if (entryPointDataRef.current?.selectedPrompt) {
                                setHasSelectedPrompt(true);
                            }
                        }

                        break;
                    default:
                        break;
                }
            } else {
                setLoadingState(false);

                if (
                    event.response_type === RESPONSE_TYPES.CAMPAIGN_CONFIG_RESPONSE ||
                    event.response_type === RESPONSE_TYPES.FOLLOW_UP_MESSAGES_RESPONSE ||
                    event.response_type === MESSAGE_TYPES.USER_QUERY_RESPONSE
                ) {
                    setIsChatInitialized(true);
                }

                setMessages(prev => {
                    const newMessages = [...prev, event];

                    if (event.response_type === RESPONSE_TYPES.FOLLOW_UP_MESSAGES_RESPONSE) {
                        setLatestContextualChipIndex(newMessages.length - 1);
                        resetHiddenChips();
                    }

                    return newMessages;
                });
            }
        },
        [
            entryPointDataRef,
            setIsLoading,
            setIsChatInitialized,
            setHasSelectedPrompt,
            setMessages,
            setLatestContextualChipIndex,
            resetHiddenChips,
            setLoadingState,
            onError
        ]
    );

    useEffect(() => {
        genAISocketService.onMessage(handleSocketMessage);

        return () => {
            genAISocketService.removeMessageHandler(handleSocketMessage);
        };
    }, [handleSocketMessage]);

    useEffect(() => {
        onInitialize?.();

        return () => {
            genAISocketService.closeConnection();
        };
    }, []);

    return useMemo(
        () => ({
            socketService: genAISocketService
        }),
        []
    );
};

export default useSuperChatSocket;
